import { useState, useEffect, useRef } from "react";
import styles from "./ArticleLayout.module.scss";

import { GooseArticleQuery } from "types/api";

import ArticleBentoRecirc from "components/article/goose/ArticleBentoRecirc";
import ArticleHeader from "components/article/goose/ArticleHeader";
import ArticleLeadArt from "components/article/goose/ArticleLeadArt";
import ArticleLayoutSection from "components/article/goose/ArticleLayoutSection";
import EditorsNote from "components/article/goose/EditorsNote";
import ArticleContent from "components/article/goose/ArticleContent";
import ArticleRailAd from "components/ads/ArticleRail";
import ArticleRelatedContent from "components/article/goose/ArticleRelatedContent";
import ArticleRecirc from "components/article/goose/ArticleRecirc";
import DesktopInjectorAd from "components/ads/DesktopInjector";
import ArticleRecircFooter from "components/article/goose/ArticleRecircFooter";
import lazy from "utils/lazy";

const ArticleFoundationLine = lazy(
  () => import("components/article/goose/ArticleFoundationLine")
);
const ArticleBio = lazy(() => import("components/article/goose/ArticleBio"));
const ArticleMostPopular = lazy(
  () => import("components/article/goose/ArticleMostPopular")
);
const ArticleRelatedVideos = lazy(
  () => import("components/article/goose/ArticleRelatedVideos")
);

const ArticleLayout = (article: GooseArticleQuery["article"]) => {
  // add any embed script tags to the page, if needed
  useEffect(() => {
    if (article.embeds.length) {
      for (const embed of article.embeds) {
        if (embed?.type === "script") {
          const scriptEl = document.createElement("script");
          scriptEl.type = "text/javascript";
          scriptEl.src = embed.src;
          document.body.appendChild(scriptEl);
        }
      }
    }
  }, [article.embeds]);

  // calculate the height of the first section to prevent loading tall ads
  // TODO: remove ollie reference before merging
  // https://github.com/theatlantic/Atlantic-CMS/blob/develop/frontend/theatlantic/js/utils/ads.js#L86-L105
  const firstSectionRef = useRef<HTMLDivElement>(null!);
  const [boxAdOnly, setBoxAdOnly] = useState(false);
  useEffect(() => {
    if (firstSectionRef.current) {
      const firstSectionDiv = firstSectionRef.current;
      const height = firstSectionDiv.getBoundingClientRect().height;
      // TODO: get more information as to the specific height value
      if (height < 1020) {
        setBoxAdOnly(true);
      }
    }
  }, [firstSectionRef]);

  const sectionsLength = article.htmlContentSections.length;
  return (
    <>
      {article.editorialProject &&
        article.editorialProject.river?.edges?.length > 3 && (
          <ArticleBentoRecirc {...article.editorialProject} />
        )}
      <main
        className={styles.root}
        id="main-content"
        data-category="story page"
      >
        <article className={styles.article}>
          <header>
            <ArticleHeader {...article} />
          </header>

          {article.htmlContentSections.map((section, i) => {
            const sectionCount = i + 1;
            const isFirst = sectionCount === 1;
            const isLast = sectionCount === sectionsLength;
            return (
              section && (
                <ArticleLayoutSection
                  ref={isFirst ? firstSectionRef : undefined}
                  key={`section-${article.id}-${sectionCount}`}
                  index={`article section ${sectionCount}`}
                  introContent={
                    /* The first layout section gets the lead art and the editor's note above the article body content. */
                    isFirst && (
                      <>
                        {article.leadArt && (
                          <ArticleLeadArt {...article.leadArt} />
                        )}
                        {article.editorsNote && (
                          <EditorsNote {...article.editorsNote} />
                        )}
                      </>
                    )
                  }
                  sectionedContent={
                    <>
                      <ArticleContent {...section} />
                      {
                        /* On articles that are part of a series with 4 or more other articles, the inline  recirc module goes between the first and second layout sections  */
                        isFirst &&
                          !isLast &&
                          article.editorialProject &&
                          article.editorialProject.river?.edges?.length > 3 && (
                            <ArticleRecirc
                              displayName={"More in this series"}
                              articles={article.editorialProject.river}
                              showImages={true}
                              position={"inline"}
                            />
                          )
                      }
                      {
                        /* On articles that are NOT part of a series, the inline  recirc module goes between the first and second layout sections  */
                        isFirst && !isLast && !article.editorialProject && (
                          <ArticleRelatedContent
                            id={article.id.split(":")[1]}
                            position={"inline"}
                            preview={article.preview}
                          />
                        )
                      }
                      {/* The last layout section gets the article-end marker, the foundation line, and the author bio(s) below the article body content */}
                      {isLast && (
                        <>
                          <div id="article-end" className={styles.articleEnd} />
                          {article.editorialProject?.foundationLineArticle && (
                            <ArticleFoundationLine
                              {...article.editorialProject}
                            />
                          )}
                          <ArticleRelatedVideos
                            id={article.id.split(":")[1]}
                            preview={article.preview}
                          />
                          {article.authors && <ArticleBio {...article} />}
                        </>
                      )}
                    </>
                  }
                  rightRail={
                    <>
                      {section.hasRailAd && (
                        <ArticleRailAd
                          boxAdOnly={isFirst && boxAdOnly}
                          targetingPos={"boxrr" + sectionCount}
                          targetingNative={isFirst ? undefined : "standard"}
                          label={!isFirst}
                        />
                      )}
                      {/* On articles that are part of a series, the first section gets a right rail recirc module instead of the bento recirc header if there are fewer than 4 other articles in the series */}
                      {isFirst &&
                        article.editorialProject &&
                        article.editorialProject.river?.edges?.length <= 3 && (
                          <ArticleRecirc
                            displayName={"More in this series"}
                            articles={article.editorialProject.river}
                            showImages={false}
                            position={"rightRail"}
                          />
                        )}
                      {/* On articles that are NOT part of a series, the first section gets the related content module */}
                      {isFirst && !article.editorialProject && (
                        <ArticleRelatedContent
                          id={article.id.split(":")[1]}
                          position={"rightRail"}
                        />
                      )}
                    </>
                  }
                  injectedContent={
                    /* The last section DOES NOT get an injector ad below it */
                    !isLast && (
                      <DesktopInjectorAd
                        targetingPos={"boxinjector-d" + sectionCount}
                        targetingNative="standard,gift"
                        className="s-native s-native--standard"
                      />
                    )
                  }
                />
              )
            );
          })}
        </article>
        <ArticleRecircFooter main={<ArticleMostPopular />} />
      </main>
    </>
  );
};

export default ArticleLayout;
